import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { Bullet16, Enter } from '../icon/icon-map';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';

export interface PhxCommentFieldReturn {
  value: string;
  checkboxes?: { [label: string]: boolean };
}

@Component({
  selector: 'phx-comment-field',
  templateUrl: './phx-comment-field.component.html',
  styleUrls: ['./phx-comment-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxCommentFieldComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-comment-field'
  }
})
export class PhxCommentFieldComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() public phxValue: string = '';
  @Input() public phxPlaceholder: string;
  @Input() public phxError: string;
  @Input() public phxSubtext: string;
  @Input() public phxCheckboxLabels: string[];
  @Input() public phxDisabled: boolean = false;

  @Output() public phxOnSave: EventEmitter<PhxCommentFieldReturn> = new EventEmitter<PhxCommentFieldReturn>();
  @Output() public phxOnCancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() public phxValueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public phxCheckboxChange: EventEmitter<{ label: string, checked: boolean }> = new EventEmitter<{ label: string, checked: boolean }>();

  public readonly Bullet16Icon: string = Bullet16;
  public readonly EnterIcon: string = Enter;
  public isEditMode: boolean = false;
  public isFocused: boolean = false;
  public _value: string = '';

  private initialValue: string = '';
  private onChangeCallback: (val: string) => void = () => {};
  private onTouchedCallback: () => void = () => {};
  private destroy$: Subject<void> = new Subject<void>();
  private el: HTMLElement;
  private textarea: HTMLTextAreaElement;
  private checkboxes: { [label: string]: boolean } = {};

  private set value(val: string) {
    if (this.value !== val) {
      this._value = val;
      this.onChangeCallback(val);
    }
  }

  private get value(): string {
    return this._value;
  }

  public get hasValueChanged(): boolean {
    return this.value !== this.initialValue;
  }

  constructor(
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    this.el = this.elRef.nativeElement;
  }

  ngOnInit(): void {
    if (this.phxValue) {
      this._value = this.phxValue;
      this.initialValue = this.phxValue;
      this.isEditMode = true;
    }
    this.textarea = this.el.querySelector('textarea') as HTMLTextAreaElement;
    this.setTextareaStyle();
  }

  ngOnChanges({ phxValue }: SimpleChanges): void {
    if (phxValue && !phxValue.firstChange) {
      const newValue = phxValue.currentValue ?? '';
      this._value = newValue;
      this.initialValue = newValue;
      this.isEditMode = !!newValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- ControlValueAccessor ---

  public writeValue(value: string): void {
    if (value !== null && value !== undefined) {
      this._value = value;
      this.initialValue = value;
      this.isEditMode = !!value;
    } else {
      this._value = '';
      this.initialValue = '';
      this.isEditMode = false;
    }
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: (val: string) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.phxDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  public onTouched(): void {
    this.onTouchedCallback();
    this.cdr.markForCheck();
  }

  // --- Template event handlers ---

  public updateModel(val: string): void {
    this.value = val;
    this.phxValue = val;
    this.phxValueChange.emit(val);
  }

  public onTextareaInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.setTextareaStyle();
    this.updateModel(textarea.value);
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === KeyboardKeys.ENTER && !event.shiftKey) {
      event.preventDefault();
      this.onSave();
    } else if (event.key === KeyboardKeys.ESC && this.isEditMode) {
      event.preventDefault();
      this.onCancel();
    }
  }

  public onSave(): void {
    if (!this.hasValueChanged) return;
    this.phxOnSave.emit({ value: this.value, checkboxes: this.checkboxes });
    this.clearField();
  }

  public onCancel(): void {
    this.phxOnCancel.emit();
    this.resetField();
  }

  public onCheckboxChange(label: string, checked: boolean): void {
    this.checkboxes[label] = checked;
    this.phxCheckboxChange.emit({ label, checked });
  }

  public onContainerClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.phxDisabled || target.closest('.phx-comment-field-extras') || target.classList.contains('.phx-comment-field-actions-visible')) return;
    this.isFocused = true;
    this.textarea.focus();
  }

  public onContainerBlur(): void {
    if (this.phxDisabled) return;
    this.isFocused = false;
    this.onTouched();
  }

  // --- Private helpers ---

  private initCheckboxes(): void {
    if (!this.phxCheckboxLabels || !this.phxCheckboxLabels.length) return;
    this.checkboxes = {};
    this.phxCheckboxLabels.forEach(label => {
      this.checkboxes[label] = false;
    });
  }

  private resetField(): void {
    this.value = this.initialValue;
    this.phxValue = this.initialValue;
    this.phxValueChange.emit(this.initialValue);
    this.reset();
  }

  private clearField(): void {
    this.value = '';
    this.phxValue = '';
    this.initialValue = '';
    this.isEditMode = false;
    this.phxValueChange.emit('');
    if (this.textarea) {
      this.textarea.value = '';
    }
    this.reset();
  }

  private reset(): void {
    this.isFocused = false;
    if (this.textarea) {
      this.textarea.blur();
    }
    this.setTextareaStyle();
    this.initCheckboxes();
  }

  private setTextareaStyle(): void {
    if (!this.textarea) return;
    this.textarea.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(this.textarea).lineHeight);
    const maxHeight = lineHeight * 4;
    if (this.textarea.scrollHeight > maxHeight)
      this.textarea.style.height = `${maxHeight}px`;
    else
      this.textarea.style.height = `${this.textarea.scrollHeight}px`;
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.onContainerBlur();
    } else {
      this.onContainerClick(event);
    }
  }
}